import { PERMISSIONS, terraContract } from '@terra/shared-authorization';
import { z } from 'zod';
import {
  ReportsFindingsBodySchema,
  ReportsFindingsByFilterBodySchema,
  ReportsTooManyMatchesResponseSchema,
  ReportsReturnSchema,
  ReportsBodySchema,
  ReportFilesResponseSchema,
  ReportFileUploadResponseSchema,
  ReportFilesBodySchema,
  ReportFilesQuerySchema,
  ReportsIdSchema,
  ReportsListPageQuerySchema,
  ReportFileIdSchema,
  ApplicationIdSchema,
  ReportDeleteSchema,
  ReportFilePasswordResponseSchema,
  UpdateReportFileStatusBodySchema,
  UpdateReportFileStatusResponseSchema,
  OnDemandReportBodySchema,
  OnDemandReportPreviewBodySchema,
  OnDemandReportPreviewResponseSchema,
  OnDemandReportGenerateResponseSchema,
  MultiAppOnDemandReportBodySchema,
  MultiAppOnDemandReportPreviewBodySchema,
} from './reports.schema';
import { REPORTS_PATH_NAME } from './reports.constants';

export const ReportsContract = terraContract
  .prefix(`/${REPORTS_PATH_NAME}`)
  .router({
    getAllReports: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_FINDINGS.name],
      })
      .route({
        method: 'GET',
        path: '/',
        summary: 'List all reports',
        description:
          'Retrieves all security reports across all applications. Reports are comprehensive security assessments that compile findings, analysis, and recommendations.',
        inputStructure: 'detailed',
      })
      .output(ReportsReturnSchema.array()),
    getReportsByApplicationId: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_FINDINGS.name],
      })
      .route({
        method: 'GET',
        path: '/application/{id}',
        summary: 'List reports by application',
        description:
          'Retrieves all security reports for a specific application, ordered by creation date.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ReportsIdSchema,
          query: ReportsListPageQuerySchema.optional(),
        }),
      )
      .output(ReportsReturnSchema.array()),
    getReport: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_FINDINGS.name],
      })
      .route({
        method: 'GET',
        path: '/id/{id}',
        summary: 'Get report details',
        description:
          'Retrieves complete details for a single report including all associated findings, executive summary, and recommendations.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: ReportsIdSchema }))
      .output(ReportsReturnSchema),
    createReport: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_FINDINGS.name],
      })
      .route({
        method: 'POST',
        path: '/',
        summary: 'Create report',
        description:
          'Creates a new security report. Reports can be created empty and have findings added later, or created with findings included.',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: ReportsBodySchema }))
      .output(ReportsReturnSchema),
    updateReport: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_FINDINGS.name],
      })
      .route({
        method: 'PATCH',
        path: '/{id}',
        summary: 'Update report',
        description:
          'Updates report properties including title, description, executive summary, and scope.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: ReportsIdSchema, body: ReportsBodySchema }))
      .output(ReportsReturnSchema),
    deleteReport: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_FINDINGS.name],
      })
      .route({
        method: 'DELETE',
        path: '/{id}',
        summary: 'Delete report',
        description:
          'Permanently deletes a report and all associated files. This action cannot be undone. Customers may only delete on-demand reports.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: ReportsIdSchema }))
      .output(ReportDeleteSchema),
    addFindingsToReportByFilter: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_FINDINGS.name],
      })
      .route({
        method: 'POST',
        path: '/{reportId}/findings-by-filter',
        summary: 'Add every finding matching a filter to a report',
        description:
          'Resolves the same filter the findings list endpoint uses, optionally subtracts excludedIds, and adds the result to the report. Rejects with 409 when the resolved set exceeds the server-side hard limit.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            reportId: z.string().describe('Report identifier'),
          }),
          body: ReportsFindingsByFilterBodySchema,
        }),
      )
      .output(ReportsReturnSchema)
      .errors({
        CONFLICT: { status: 409, data: ReportsTooManyMatchesResponseSchema },
      }),
    addFindingsToReport: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_FINDINGS.name],
      })
      .route({
        method: 'POST',
        path: '/{reportId}',
        summary: 'Add findings to report',
        description:
          'Adds security findings to an existing report. Findings will be included in the generated PDF output.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            reportId: z.string().describe('Report identifier'),
          }),
          body: ReportsFindingsBodySchema,
        }),
      )
      .output(ReportsReturnSchema),
    generateReportWithEnrichment: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_FINDINGS.name],
      })
      .route({
        method: 'POST',
        path: '/application/{applicationId}/generate',
        summary: 'Generate AI-enriched report',
        description:
          'Generates a comprehensive security report with AI-powered enrichment. The AI analyzes findings and generates strategic recommendations, executive summaries, and remediation priorities. Returns a jobId for tracking progress via SSE.',
        inputStructure: 'detailed',
        successStatus: 202,
      })
      .input(z.object({ params: ApplicationIdSchema, body: ReportsBodySchema }))
      .output(
        z.object({
          message: z.string().describe('Status message'),
          jobId: z.string().describe('Job ID for tracking progress via SSE'),
        }),
      ),
    previewOnDemandReportFindings: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_FINDINGS.name],
      })
      .route({
        method: 'POST',
        path: '/application/{applicationId}/generate-on-demand/preview',
        summary: 'Preview on-demand report finding count',
        description:
          'Resolves published findings for an application and timeframe and returns the matched count against the PDF generation limit.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationIdSchema,
          body: OnDemandReportPreviewBodySchema,
        }),
      )
      .output(OnDemandReportPreviewResponseSchema),
    generateOnDemandReport: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_FINDINGS.name],
      })
      .route({
        method: 'POST',
        path: '/application/{applicationId}/generate-on-demand',
        summary: 'Generate on-demand report with PDF',
        description:
          'Resolves findings by timeframe, runs AI enrichment, then enqueues PDF generation. Returns a jobId for tracking progress via SSE.',
        inputStructure: 'detailed',
        successStatus: 202,
      })
      .input(
        z.object({
          params: ApplicationIdSchema,
          body: OnDemandReportBodySchema,
        }),
      )
      .output(OnDemandReportGenerateResponseSchema)
      .errors({
        CONFLICT: { status: 409, data: ReportsTooManyMatchesResponseSchema },
      }),
    previewMultiAppOnDemandReportFindings: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_FINDINGS.name],
      })
      .route({
        method: 'POST',
        path: '/generate-on-demand/preview',
        summary: 'Preview multi-application on-demand report finding count',
        description:
          'Resolves published findings across selected applications and a timeframe and returns the matched count against the PDF generation limit.',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: MultiAppOnDemandReportPreviewBodySchema }))
      .output(OnDemandReportPreviewResponseSchema),
    generateMultiAppOnDemandReport: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_FINDINGS.name],
      })
      .route({
        method: 'POST',
        path: '/generate-on-demand/generate',
        summary: 'Generate multi-application on-demand report with PDF',
        description:
          'Generates an Executive Summary (or other multi-app) report across selected applications, then enqueues PDF generation. Returns a jobId for tracking progress via SSE.',
        inputStructure: 'detailed',
        successStatus: 202,
      })
      .input(z.object({ body: MultiAppOnDemandReportBodySchema }))
      .output(OnDemandReportGenerateResponseSchema)
      .errors({
        CONFLICT: { status: 409, data: ReportsTooManyMatchesResponseSchema },
      }),
    getAllReportFiles: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_FINDINGS.name],
      })
      .route({
        method: 'GET',
        path: '/files',
        summary: 'List all report files',
        description:
          'Retrieves all PDF report files across all applications. Files include both draft and finalized reports. Optionally filter by source (Manual or Scheduled).',
        inputStructure: 'detailed',
      })
      .input(z.object({ query: ReportFilesQuerySchema }))
      .output(ReportFilesResponseSchema),
    getReportFilesByApplicationId: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_FINDINGS.name],
      })
      .route({
        method: 'GET',
        path: '/application/{applicationId}/files',
        summary: 'List report files by application',
        description:
          'Retrieves all PDF report files for a specific application. Optionally filter by source (Manual or Scheduled).',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationIdSchema,
          query: ReportFilesQuerySchema,
        }),
      )
      .output(ReportFilesResponseSchema),
    getReportFiles: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_FINDINGS.name],
      })
      .route({
        method: 'GET',
        path: '/{id}/files',
        summary: 'List files for report',
        description:
          'Retrieves all PDF files associated with a specific report.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: ReportsIdSchema }))
      .output(ReportFilesResponseSchema),
    uploadReportFiles: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_FINDINGS.name],
      })
      .route({
        method: 'POST',
        path: '/{id}/files',
        summary: 'Upload report files',
        description:
          'Uploads PDF files to a report. Files are automatically encrypted and can be password protected for secure sharing with clients.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: ReportsIdSchema, body: ReportFilesBodySchema }))
      .output(ReportFileUploadResponseSchema),
    deleteReportFile: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_FINDINGS.name],
      })
      .route({
        method: 'DELETE',
        path: '/{id}/files/{fileId}',
        summary: 'Delete report file',
        description:
          'Removes a specific PDF file from a report. Customers may only delete files belonging to on-demand reports.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: ReportFileIdSchema }))
      .output(ReportDeleteSchema),
    getReportFilePassword: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_FINDINGS.name],
      })
      .route({
        method: 'GET',
        path: '/{id}/files/{fileId}/password',
        summary: 'Get file password',
        description:
          'Retrieves the password for a password-protected PDF report file. Use this when sharing reports with clients.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: ReportFileIdSchema }))
      .output(ReportFilePasswordResponseSchema),
    updateReportFileStatus: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_FINDINGS.name],
      })
      .route({
        method: 'PATCH',
        path: '/{id}/files/{fileId}/status',
        summary: 'Update file status',
        description:
          'Updates the status of a report file between DRAFT and FINAL. Final reports are locked from further modifications.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ReportFileIdSchema,
          body: UpdateReportFileStatusBodySchema,
        }),
      )
      .output(UpdateReportFileStatusResponseSchema),
  });
